'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

/* ─── DATA ─────────────────────────────────────────────── */

const orderLifecycle = [
  { icon: 'DevicePhoneMobileIcon', label: 'Order Placed', sub: 'App / Web' },
  { icon: 'BuildingStorefrontIcon', label: 'Restaurant Confirms', sub: 'Auto / manual accept' },
  { icon: 'ClockIcon', label: 'Kitchen Prepares', sub: 'Real-time prep timer' },
  { icon: 'UserIcon', label: 'Rider Picks Up', sub: 'GPS dispatched' },
  { icon: 'MapPinIcon', label: 'Delivered', sub: 'OTP / photo proof' },
];

const coreFeatures = [
  {
    num: '01',
    icon: 'BuildingStorefrontIcon',
    label: 'Restaurant & Menu Management Portal',
    desc: 'A full-featured web and app portal for restaurant owners — build menus with categories, variants, add-ons, and photos; manage operating hours, stock availability, and incoming orders from one screen.',
    pills: ['Menu builder', 'Order dashboard', 'KDS view', 'Prep timer', 'Out-of-stock toggle'],
    color: '#B45309',
    bg: 'rgba(180,83,9,0.08)',
  },
  {
    num: '02',
    icon: 'DevicePhoneMobileIcon',
    label: 'Customer App with Live Order Tracking',
    desc: 'Customers browse restaurants, explore menus with dietary filters, and track their order live on a map — from kitchen confirmation to rider pick-up to doorstep arrival, with push updates at every step.',
    pills: ['Live map tracking', 'Push notifications', 'Dietary filters', 'Scheduled orders', 'Reorder'],
    color: '#C2410C',
    bg: 'rgba(194,65,12,0.08)',
  },
  {
    num: '03',
    icon: 'BoltIcon',
    label: 'Smart Delivery Partner Dispatch',
    desc: 'The AI dispatch engine assigns the nearest available rider based on location, current load, and performance rating. It auto-batches nearby orders to maximise efficiency and reduce delivery cost.',
    pills: ['AI assignment', 'Auto-batching', 'Load balancing', 'Performance routing'],
    color: '#D97706',
    bg: 'rgba(217,119,6,0.08)',
  },
  {
    num: '04',
    icon: 'ClockIcon',
    label: 'Estimated Delivery Time Predictions',
    desc: "ML models factor in restaurant preparation time, rider transit distance, live traffic, and weather to generate accurate ETAs. The ETA is dynamically recalculated as the order progresses through each stage.",
    pills: ['ML-based ETA', 'Traffic-aware', 'Dynamic recalculation', 'Per-stage updates'],
    color: '#EA580C',
    bg: 'rgba(234,88,12,0.08)',
  },
  {
    num: '05',
    icon: 'CreditCardIcon',
    label: 'Multi-payment: UPI, Card, Wallet & COD',
    desc: 'Support every payment preference — UPI, credit/debit cards, digital wallets, and cash on delivery. Automated restaurant payouts, rider commissions, platform fee deductions, and GST invoicing built in.',
    pills: ['UPI & cards', 'Wallets', 'COD', 'Auto payout', 'GST invoicing'],
    color: '#B45309',
    bg: 'rgba(180,83,9,0.08)',
  },
  {
    num: '06',
    icon: 'MegaphoneIcon',
    label: 'Promotions, Offers & Loyalty Programs',
    desc: 'Run first-order discounts, flash deals, buy-X-get-Y, coupon codes, and free delivery thresholds. Reward repeat customers through a tiered loyalty points system and referral program.',
    pills: ['Flash deals', 'Coupon codes', 'Loyalty points', 'Referral rewards', 'Meal pass'],
    color: '#C2410C',
    bg: 'rgba(194,65,12,0.08)',
  },
];

const restaurantFeatures = [
  'Menu & Category Builder with Photos',
  'Real-time Order Accept / Reject',
  'Kitchen Display System (KDS) View',
  'Preparation Time Management',
  'Out-of-stock Item Toggle',
  'Operating Hours & Holiday Mode',
  'Discount & Promo Code Setup',
  'Branch & Outlet Management',
  'Sales Analytics & Revenue Reports',
  'Customer Ratings & Review View',
  'Tax Configuration & Invoice Export',
  'Staff Role & Access Control',
];

const customerAppFeatures = [
  'Discover Restaurants Nearby',
  'Filter by Cuisine, Rating & Price',
  'Dietary & Allergen Filters',
  'Live Order Tracking on Map',
  'Real-time Delivery ETA Updates',
  'Scheduled & Future Orders',
  'Reorder Favourites (One Tap)',
  'Multiple Saved Addresses',
  'In-app Support Chat',
  'Loyalty Points & Referral Rewards',
  'Dark Mode & Multi-language',
  'Contactless Delivery Option',
];

const riderAppFeatures = [
  'Online / Offline Toggle',
  'Accept / Reject Delivery Requests',
  'Optimised Route Navigation',
  'Live Order Status Updates',
  'Proof of Delivery (OTP / Photo)',
  'Earnings Dashboard',
  'Daily & Weekly Payout Tracker',
  'Performance Score & Badges',
  'Incentive & Bonus Alerts',
  'Customer Call Masking',
  'Batch Order Management',
  'Trip History & Earnings Report',
];

const promotionTypes = [
  { icon: 'TagIcon', label: 'First Order Discount', desc: 'Acquire new customers with a guaranteed saving on their first order.' },
  { icon: 'BoltIcon', label: 'Flash Deals', desc: 'Time-limited offers that create urgency and spike order volume during off-peak hours.' },
  { icon: 'TicketIcon', label: 'Coupon Codes', desc: 'Merchant-specific or platform-wide codes distributed via SMS, email, or social media.' },
  { icon: 'GiftIcon', label: 'Loyalty Points', desc: 'Customers earn points per order and redeem them for discounts or free deliveries.' },
  { icon: 'UserGroupIcon', label: 'Referral Program', desc: 'Existing customers earn credit when they refer a friend who places their first order.' },
  { icon: 'StarIcon', label: 'Subscription / Meal Pass', desc: 'Monthly plans offering free delivery, extra discounts, and priority service for subscribers.' },
];

const analyticsFeatures = [
  { icon: 'ChartBarIcon', label: 'Daily Orders & Revenue', sub: 'Real-time sales dashboard with hourly breakdown' },
  { icon: 'FireIcon', label: 'Best-selling Items', sub: 'Top dishes by volume and revenue contribution' },
  { icon: 'ArrowPathIcon', label: 'Customer Repeat Rate', sub: 'Track returning customers and order frequency' },
  { icon: 'CurrencyRupeeIcon', label: 'Average Order Value', sub: 'Monitor AOV trends and upsell performance' },
  { icon: 'XCircleIcon', label: 'Cancellation Analysis', sub: 'Identify cancellation reasons and reduce leakage' },
  { icon: 'MapPinIcon', label: 'Zone-wise Delivery Stats', sub: 'Delivery time and cost performance per area' },
];

const additionalFeatures = [
  { icon: 'BellAlertIcon', label: 'Smart Push Notifications', sub: 'Order updates, promos & re-engagement' },
  { icon: 'ShieldCheckIcon', label: 'Fraud & Refund Control', sub: 'Flag suspicious orders, manage refunds' },
  { icon: 'CalendarDaysIcon', label: 'Scheduled Order Delivery', sub: 'Pre-book meals for specific time slots' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'WhatsApp Order Booking', sub: 'Order via chatbot, no app needed' },
  { icon: 'DocumentTextIcon', label: 'Digital Invoicing & GST', sub: 'Auto-generated bills for B2B & B2C orders' },
  { icon: 'AdjustmentsHorizontalIcon', label: 'Zone & Area Management', sub: 'Define delivery radius per restaurant' },
];

const stats = [
  { value: '3 Apps', label: 'Restaurant · Customer · Rider', icon: 'DevicePhoneMobileIcon' },
  { value: 'AI Dispatch', label: 'Smart Rider Assignment', icon: 'BoltIcon' },
  { value: 'ML-ETA', label: 'Accurate Delivery Predictions', icon: 'ClockIcon' },
  { value: '21 Days', label: 'Go-Live Timeline', icon: 'RocketLaunchIcon' },
];

/* ─── COMPONENT ────────────────────────────────────────── */

export default function FoodDeliveryContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #3B1002 0%, #7C2D12 45%, #B45309 100%)' }}
      >
        <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
        <div className="orb absolute w-[700px] h-[500px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(249,115,22,0.7) 0%, transparent 65%)', top: '-150px', right: '-100px' }} />
        <div className="orb-2 absolute w-[400px] h-[400px] rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(180,83,9,0.8) 0%, transparent 70%)', bottom: '-80px', left: '-80px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/50 text-sm font-medium mb-7 hover:text-white/80 transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FED7AA]/40 bg-[#FED7AA]/10 text-[11px] font-semibold tracking-widest text-[#FED7AA] uppercase mb-5">
                <Icon name="FireIcon" size={11} className="text-[#FB923C]" />
                Food Delivery Platform
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-5 mt-2">
                Your branded food{' '}
                <span className="italic font-light" style={{ color: '#FED7AA' }}>
                  delivery platform,
                </span>
                <br />
                <span style={{ color: '#FB923C' }}>done right.</span>
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/60 text-lg leading-relaxed mb-9 max-w-lg">
                A complete end-to-end food delivery ecosystem — restaurant portal, customer app, rider app, AI dispatch, ML-ETA, and a promotions engine — all white-label ready.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link href="/demo-booking" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold text-[#431407] hover:opacity-90 shadow-lg transition-all duration-200 hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #FED7AA, #FB923C)' }}>
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/75 border border-white/20 hover:border-white/45 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['21-day go-live', 'White-label ready', '3 dedicated apps', 'WhatsApp ordering'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/45 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#FB923C]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — order lifecycle panel */}
            <div className="animate-on-scroll stagger-3">
              <div
                className="rounded-2xl overflow-hidden border border-white/10"
                style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(16px)' }}
              >
                {/* Panel header */}
                <div className="px-6 py-4 border-b border-white/[0.08] flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-white/35 uppercase tracking-widest">Live Order Lifecycle</span>
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#4ADE80] opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4ADE80]" />
                    </span>
                    <span className="text-xs text-white/40 font-medium">All systems live</span>
                  </div>
                </div>

                {/* Lifecycle steps */}
                <div className="p-5 flex flex-col gap-0">
                  {orderLifecycle.map((step, i) => (
                    <div key={step.label} className="flex items-start gap-4">
                      {/* Timeline */}
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center z-10"
                          style={{ background: i < 4 ? 'linear-gradient(135deg, #B45309, #D97706)' : 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}
                        >
                          <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={16} className={i < 4 ? 'text-white' : 'text-white/40'} />
                        </div>
                        {i < orderLifecycle.length - 1 && (
                          <div className="w-0.5 h-6 my-1" style={{ background: i < 3 ? 'rgba(180,83,9,0.5)' : 'rgba(255,255,255,0.1)' }} />
                        )}
                      </div>
                      {/* Content */}
                      <div className="flex-1 pb-1 pt-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm font-semibold ${i < 4 ? 'text-white' : 'text-white/40'}`}>{step.label}</p>
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${i < 4 ? 'text-[#FED7AA] bg-[rgba(180,83,9,0.25)]' : 'text-white/30 bg-white/5'}`}>
                            {step.sub}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Summary strip */}
                <div className="px-5 py-4 border-t border-white/[0.08] grid grid-cols-3 gap-3">
                  {[
                    { icon: 'BuildingStorefrontIcon', label: 'Restaurant Portal' },
                    { icon: 'DevicePhoneMobileIcon', label: 'Customer App' },
                    { icon: 'UserIcon', label: 'Rider App' },
                  ].map((a) => (
                    <div key={a.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg bg-white/[0.05] border border-white/[0.08]">
                      <Icon name={a.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-[#FB923C]" />
                      <span className="text-[10px] font-medium text-white/55 text-center leading-tight">{a.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #431407 0%, #7C2D12 60%, #92400E 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#FED7AA]" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/55 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(180,83,9,0.3)] bg-[rgba(180,83,9,0.07)] text-[11px] font-semibold tracking-widest text-[#B45309] uppercase mb-4">
              Key Features
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six features that power{' '}
              <span className="italic font-light text-[#B45309]">every food order.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">From restaurant onboarding to doorstep delivery — every stakeholder covered, every stage automated.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl p-7 border transition-all duration-300 hover:shadow-lg`}
                style={{ background: i % 2 === 0 ? '#FFFBEB' : 'white', borderColor: 'rgba(180,83,9,0.12)' }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <span
                    className="font-mono-custom text-[11px] font-bold w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #B45309, #D97706)' }}
                  >
                    {f.num}
                  </span>
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                    style={{ background: f.bg }}
                  >
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} style={{ color: f.color }} />
                  </div>
                  <h3 className="font-display text-base font-semibold text-[#0F172A] leading-snug pt-1 flex-1">{f.label}</h3>
                </div>
                <p className="text-sm text-[#475569] leading-relaxed mb-4">{f.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {f.pills.map((p) => (
                    <span key={p} className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-[#B45309]" style={{ background: 'rgba(180,83,9,0.08)' }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ORDER JOURNEY (dark warm) ── */}
      <div className="py-24 relative overflow-hidden" style={{ background: '#3B1002' }}>
        <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(180,83,9,0.4) 0%, transparent 70%)', filter: 'blur(40px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FED7AA]/30 bg-[#FED7AA]/10 text-[11px] font-semibold tracking-widest text-[#FED7AA] uppercase mb-5">
              Order Journey
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">
              Tap to order.{' '}
              <span className="italic font-light text-[#FED7AA]">Delivered at the door.</span>
            </h2>
          </div>

          {/* 5-step pipeline */}
          <div className="relative grid sm:grid-cols-5 gap-4">
            <div className="hidden sm:block absolute top-[26px] left-[calc(10%+20px)] right-[calc(10%+20px)] h-px"
              style={{ background: 'linear-gradient(90deg, rgba(251,146,60,0.05), rgba(251,146,60,0.35) 20%, rgba(251,146,60,0.35) 80%, rgba(251,146,60,0.05))' }} />

            {orderLifecycle.map((step, i) => (
              <div key={step.label} className={`animate-on-scroll stagger-${i + 2} flex flex-col items-center text-center`}>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 z-10"
                  style={{ background: 'linear-gradient(135deg, #B45309, #D97706)', boxShadow: '0 0 28px rgba(180,83,9,0.45)' }}
                >
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                </div>
                <div className="text-[10px] font-bold text-[#FB923C]/50 tracking-widest uppercase mb-1.5">Step {i + 1}</div>
                <h3 className="font-semibold text-white text-sm mb-1.5">{step.label}</h3>
                <p className="text-xs text-white/45">{step.sub}</p>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 mt-14 flex flex-wrap justify-center gap-3">
            {['Real-time Order Updates', 'ML-powered ETA', 'Live Map Tracking', 'AI Rider Dispatch', 'OTP / Photo POD'].map((p) => (
              <div key={p} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#FB923C]/20 bg-white/[0.04]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FB923C]" />
                <span className="text-sm text-white/60 font-medium">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3 APPS (Restaurant / Customer / Rider) ── */}
      <div className="py-24" style={{ background: '#FFF7ED' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(180,83,9,0.3)] bg-[rgba(180,83,9,0.07)] text-[11px] font-semibold tracking-widest text-[#B45309] uppercase mb-4">
              Platform Apps
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Three purpose-built apps.{' '}
              <span className="italic font-light text-[#B45309]">One ecosystem.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Restaurant owners, customers, and delivery riders each get an app built precisely for their role.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Restaurant Portal */}
            <div className="animate-on-scroll stagger-2 rounded-2xl overflow-hidden border border-[rgba(180,83,9,0.15)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #7C2D12, #B45309)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="BuildingStorefrontIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Restaurant Portal</h3>
                    <p className="text-white/60 text-xs">Web + App</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-white">
                {restaurantFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-[#FFF7ED] border border-[rgba(180,83,9,0.08)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(180,83,9,0.12)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#B45309]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer App */}
            <div className="animate-on-scroll stagger-3 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6 bg-[#0F172A]">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                    <Icon name="DevicePhoneMobileIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Customer App</h3>
                    <p className="text-white/50 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-white">
                {customerAppFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-[#F5F5F5] border border-[rgba(15,23,42,0.06)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center bg-[#0F172A]">
                      <Icon name="CheckIcon" size={9} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Rider App */}
            <div className="animate-on-scroll stagger-4 rounded-2xl overflow-hidden border border-[rgba(180,83,9,0.15)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #92400E, #D97706)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="UserIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Rider App</h3>
                    <p className="text-white/60 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-white">
                {riderAppFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-[#FFFBEB] border border-[rgba(180,83,9,0.08)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(180,83,9,0.15)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#B45309]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── PROMOTIONS & LOYALTY ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(180,83,9,0.3)] bg-[rgba(180,83,9,0.07)] text-[11px] font-semibold tracking-widest text-[#B45309] uppercase mb-4">
              Promotions & Loyalty
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Acquire, retain, and reward{' '}
              <span className="italic font-light text-[#B45309]">every customer.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">A built-in promotions engine that handles every growth lever — from first-order acquisition to long-term loyalty.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {promotionTypes.map((p, i) => (
              <div
                key={p.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl p-7 border border-[rgba(180,83,9,0.1)] hover:border-[rgba(180,83,9,0.3)] hover:shadow-md transition-all duration-300`}
                style={{ background: i % 3 === 1 ? '#FFFBEB' : 'white' }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                  style={{ background: 'linear-gradient(135deg, rgba(180,83,9,0.12), rgba(217,119,6,0.06))' }}
                >
                  <Icon name={p.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#B45309]" />
                </div>
                <h3 className="font-display text-base font-semibold text-[#0F172A] mb-2">{p.label}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RESTAURANT ANALYTICS ── */}
      <div className="py-24" style={{ background: '#FFF7ED' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(180,83,9,0.3)] bg-[rgba(180,83,9,0.07)] text-[11px] font-semibold tracking-widest text-[#B45309] uppercase mb-5">
                Analytics
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-5">
                Data that helps restaurants{' '}
                <span className="italic font-light text-[#B45309]">grow faster.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                Every restaurant gets a real-time analytics dashboard that breaks down orders, revenue, best-sellers, and customer behaviour — so decisions are driven by data, not guesswork.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  'Revenue trend charts — daily, weekly, monthly',
                  'Top dishes ranked by volume and revenue',
                  'Customer retention and repeat order rate',
                  'Peak hour analysis for staff planning',
                  'Zone-wise delivery time and success rate',
                  'Rider performance per restaurant',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #B45309, #D97706)' }}>
                      <Icon name="CheckIcon" size={11} className="text-white" />
                    </div>
                    <span className="text-sm text-[#475569]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — analytics feature cards */}
            <div className="animate-on-scroll stagger-2 grid grid-cols-2 gap-4">
              {analyticsFeatures.map((a, i) => (
                <div
                  key={a.label}
                  className={`animate-on-scroll stagger-${i + 2} rounded-2xl p-5 border border-[rgba(180,83,9,0.12)] hover:border-[rgba(180,83,9,0.3)] hover:shadow-md transition-all group bg-white`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'rgba(180,83,9,0.08)' }}>
                    <Icon name={a.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#B45309]" />
                  </div>
                  <p className="font-semibold text-sm text-[#0F172A] mb-1">{a.label}</p>
                  <p className="text-xs text-[#64748B] leading-relaxed">{a.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── ADDITIONAL FEATURES ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(180,83,9,0.3)] bg-[rgba(180,83,9,0.07)] text-[11px] font-semibold tracking-widest text-[#B45309] uppercase mb-4">
              More Capabilities
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Everything else your{' '}
              <span className="italic font-light text-[#B45309]">food business needs.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {additionalFeatures.map((f, i) => (
              <div
                key={f.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} flex items-center gap-4 p-6 rounded-2xl border border-[rgba(180,83,9,0.1)] bg-[#FFFBEB] hover:border-[rgba(180,83,9,0.3)] hover:bg-white hover:shadow-md transition-all duration-300 group`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                  style={{ background: 'linear-gradient(135deg, rgba(180,83,9,0.12), rgba(217,119,6,0.06))' }}
                >
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#B45309]" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0F172A]">{f.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {['White-label Ready', 'Cloud-based Platform', 'Real-time Tracking', 'Secure & Reliable'].map((item) => (
              <div key={item} className="flex items-center gap-2 p-3 bg-[#FFF7ED] rounded-xl border border-[rgba(180,83,9,0.1)]">
                <Icon name="CheckBadgeIcon" size={16} className="text-[#B45309] flex-shrink-0" />
                <span className="text-xs font-medium text-[#475569]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24" style={{ background: '#FFF7ED' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #431407 0%, #7C2D12 40%, #B45309 75%, #D97706 100%)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(251,146,60,0.25) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FED7AA]/30 bg-[#FED7AA]/10 text-[11px] font-semibold tracking-widest text-[#FED7AA] uppercase mb-6">
              <Icon name="FireIcon" size={11} className="text-[#FB923C]" />
              Ready to Launch?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Your branded food delivery platform,{' '}
              <span className="italic font-light text-[#FED7AA]">live in 21 days.</span>
            </h2>

            <p className="relative text-white/65 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/65 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/65 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/65 font-medium">
                <Icon name="GlobeAltIcon" size={14} />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold text-[#431407] hover:opacity-90 shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #FED7AA, #FB923C)' }}
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
